import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiGet, apiPost } from "@/lib/api";
import type { Esquema } from "@/lib/contrato";

import type { Combinacion } from "./catalogo-global";

/**
 * Un ítem manual: referencia de la línea «Otro» que no se fabrica, con su código propio NECA-OT-NN
 * (`code`, el código con que sale en la copia a Siigo). `last_price`: el último precio por unidad con
 * que se cotizó; se sugiere, no se impone.
 */
export type ManualItem = Esquema<"ManualItem">;

const KEY = ["quotations", "manual-items"] as const;
const PATH = "/quotations/manual-items";

/**
 * Los ítems manuales activos como combinaciones del buscador de «Añadir ítem» (`GET /quotations/manual-items`,
 * solo con el buscador abierto), con si aún cargan o fallaron.
 */
export function useItemsManuales(enabled: boolean) {
  const query = useQuery({ queryKey: KEY, queryFn: () => apiGet<ManualItem[]>(PATH), enabled });
  const items = Array.isArray(query.data) ? combinacionesDeItemsManuales(query.data) : [];
  return { items, pending: query.isPending, failed: query.isError };
}

/**
 * **Crear un ítem manual** (`cotizaciones.editar`): el servidor le da su código NECA-OT-NN; si ya existe uno
 * con ese nombre, devuelve ese mismo. Un nombre de otra referencia lo rechaza (409) y su mensaje se
 * muestra tal cual.
 */
export function useCreateManualItem() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => apiPost<ManualItem>(PATH, { name }),
    onSuccess: () => void client.invalidateQueries({ queryKey: KEY }),
  });
}

/** Un ítem manual en el buscador: línea «Otro», en unidades, con su código y el último precio cotizado. */
export function combinacionesDeItemsManuales(rows: ManualItem[]): Combinacion[] {
  return rows.map((r): Combinacion => ({
    key: `manual:${r.reference_id}`,
    line: "Otro",
    label: r.name,
    reference_id: r.reference_id,
    reference_name: r.name,
    format_name: null,
    family_variant_id: null,
    variant_name: null,
    color_id: null,
    color_code: null,
    hex: null,
    sale_units: ["und"],
    price: { und: r.last_price },
    detail: r.code,
    last_price: true,
  }));
}
